/**
 * ONE requestAnimationFrame loop for every counting number on the page.
 *
 * WHY A SHARED TICKER
 * AnimatedNumber used to own a private rAF per instance. That was fine for the
 * three headline figures it started life on (household / registration / sleeve
 * AUM), but the count-up now runs on table cells too — a holdings table is
 * easily 150 rows x 5 money columns, i.e. ~750 instances, and 750 concurrent
 * rAF callbacks is a dropped-frame machine on the exact render the user is
 * watching. One loop stepping N cheap lerps costs a rounding error by
 * comparison, and it also guarantees every number on screen advances on the
 * SAME frame — staggered rAF starts made adjacent cells visibly drift apart.
 *
 * The loop only exists while something is animating: the last finished tween
 * cancels it, and the next `startCountUp` starts it again.
 */

export interface CountUpHandle {
  /** Stop this tween early (component unmount, value superseded). */
  cancel: () => void
}

interface Tween {
  from: number
  to: number
  /** Timestamp the tween was registered — delay is measured from here. */
  born: number
  duration: number
  delay: number
  onFrame: (value: number) => void
}

const tweens = new Set<Tween>()
let raf = 0

// easeOutCubic — fast out of the gate, long settle. Matches the header figure
// the rest of the app already animates with.
const ease = (t: number) => 1 - Math.pow(1 - t, 3)

function frame(now: number) {
  for (const t of tweens) {
    const elapsed = now - t.born - t.delay
    // Still inside its stagger delay: hold at the start value, don't emit.
    if (elapsed < 0) continue

    const p = t.duration > 0 ? Math.min(1, elapsed / t.duration) : 1
    t.onFrame(t.from + (t.to - t.from) * ease(p))
    if (p >= 1) tweens.delete(t)
  }

  raf = tweens.size ? requestAnimationFrame(frame) : 0
}

function pump() {
  if (!raf && tweens.size) raf = requestAnimationFrame(frame)
}

/**
 * Whether the user asked for reduced motion. Mirrors the `html.reduce-motion`
 * class the CSS keys off, so JS tweens and CSS animations agree.
 */
export function prefersReducedMotion(): boolean {
  return import.meta.client && document.documentElement.classList.contains('reduce-motion')
}

/**
 * Tween `from` → `to`, calling `onFrame` with each intermediate value.
 *
 * Snaps straight to `to` (one final `onFrame`) when motion is reduced, on the
 * server, or when there is nothing to animate — callers never need to branch.
 */
export function startCountUp(opts: {
  from: number
  to: number
  duration?: number
  /** Milliseconds to wait before this tween starts moving. Used to walk a
   *  column of cells instead of flashing them all at once. */
  delay?: number
  onFrame: (value: number) => void
}): CountUpHandle {
  const { from, to, duration = 420, delay = 0, onFrame } = opts

  if (import.meta.server || prefersReducedMotion() || duration <= 0 || from === to) {
    onFrame(to)
    return { cancel: () => {} }
  }

  const tween: Tween = { from, to, born: performance.now(), duration, delay, onFrame }
  tweens.add(tween)
  pump()

  return {
    cancel: () => {
      tweens.delete(tween)
      if (!tweens.size && raf) {
        cancelAnimationFrame(raf)
        raf = 0
      }
    }
  }
}

/**
 * Stagger helper: turn a row index into a delay, CAPPED.
 *
 * An uncapped `index * step` means row 60 sits reading `$0.00` for a second
 * and a half after its neighbours have settled, which looks like missing data
 * rather than motion. Past the cap the whole tail starts together — by then
 * it is usually below the fold anyway.
 */
export function staggerDelay(index: number, step = 10, maxSteps = 10): number {
  if (!Number.isFinite(index) || index <= 0) return 0
  return Math.min(index, maxSteps) * step
}

/**
 * "Has this exact number already counted up?"
 *
 * A table cell's render function runs again on every sort, filter, density
 * change and column resize. Without a gate the whole grid re-counts from zero
 * each time you click a header, which reads as the data reloading — the
 * opposite of the reassurance the effect is there to give.
 *
 * The key carries the VALUE, so a genuine data change (a refresh landing new
 * market values) counts up again, while re-rendering the same numbers does
 * not. Returns true exactly once per key.
 */
const seen = new Set<string>()

export function revealOnce(key: string): boolean {
  // Server render has no animation and must not poison the client's first
  // paint — the hydrating client needs its one shot at each key.
  if (import.meta.server) return false
  if (seen.has(key)) return false

  // Cheap unbounded-growth guard. A user who has paged through enough tables
  // to reach this has long since seen the reveal; dropping the history just
  // means a future identical cell may count once more.
  if (seen.size > 5000) seen.clear()

  seen.add(key)
  return true
}
